<template>
  <div class="view-container">
    <div class="main-layout">
      <div class="left-panel">
        <div class="panel-toolbar">
          <div class="search-box">
            <i class="fas fa-search search-icon"></i>
            <input v-model="treeSearch" type="text" placeholder="搜索角色..." class="search-input">
          </div>
          <button @click="openAddDialog" class="btn btn-add">
            <i class="fas fa-plus"></i> 新增
          </button>
        </div>
        <div class="panel-body">
          <table class="role-table">
            <thead>
              <tr>
                <th>角色名称</th>
                <th width="80">操作</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(node, index) in filteredTree" :key="node.id">
                <RoleRow
                  :node="node"
                  :base-no="node.no"
                  :depth="0"
                  :selected-id="selectedRoleId"
                  :expanded-ids="expandedIds"
                  @select="selectRole"
                  @edit="openEditDialog"
                  @delete="confirmDelete"
                  @add-child="openAddDialog"
                  @toggle-expand="toggleExpand"
                />
              </template>
              <tr v-if="filteredTree.length === 0">
                <td colspan="2" class="empty-row">
                  {{ treeSearch ? '未找到匹配的角色' : '暂无角色，点击上方按钮新建' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="right-panel">
        <div class="panel-body">
          <div v-if="selectedRoleId && selectedRole">
            <div class="detail-header">
              <div class="detail-title">{{ selectedRoleName }}</div>
              <div class="detail-desc" v-if="selectedRoleDesc">{{ selectedRoleDesc }}</div>
            </div>
            <div class="detail-toolbar">
              <div class="detail-subtitle">角色成员 ({{ currentMembers.length }})</div>
              <button @click="openMemberAdd" class="btn-add-member">
                <i class="fas fa-user-plus"></i> 添加成员
              </button>
            </div>
            <div v-if="currentMembers.length > 0">
              <table class="member-table">
                <thead>
                  <tr>
                    <th class="member-check-th" width="40">
                      <input type="checkbox" :checked="isAllMemberSelected" @change="toggleAllMember">
                    </th>
                    <th>姓名</th>
                    <th>账号</th>
                    <th width="60">操作</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="member in currentMembers" :key="member.id">
                    <td class="member-check-th">
                      <input type="checkbox" :checked="selectedMemberIds.includes(member.id)" @change="toggleMemberSelect(member.id)">
                    </td>
                    <td>{{ member.name }}</td>
                    <td>{{ member.account }}</td>
                    <td>
                      <button @click="removeSingleMember(member.id)" class="btn-remove">
                        <i class="fas fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="no-members">
              <p>该角色暂无成员</p>
              <button @click="openMemberAdd" class="btn-add-member-sm">
                <i class="fas fa-user-plus"></i> 添加成员
              </button>
            </div>
            <div class="detail-footer" v-if="selectedMemberIds.length > 0">
              <button @click="batchRemoveMembers" class="btn btn-danger btn-sm">
                <i class="fas fa-trash"></i> 批量移除选中 ({{ selectedMemberIds.length }})
              </button>
            </div>
          </div>
          <div v-else class="empty-state">
            <i class="fas fa-users"></i>
            <p>请选择一个角色</p>
            <p class="hint">在左侧点击角色名称查看成员信息</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <div v-if="showFormModal" class="modal-overlay" @click.self="closeFormModal">
      <div class="modal-dialog">
        <div class="modal-header">
          <h4>{{ formTitle }}</h4>
          <button @click="closeFormModal" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>序号 (No) *</label>
            <input v-model.number="form.no" type="number" class="form-control" placeholder="请输入序号" required>
          </div>
          <div class="form-group">
            <label>角色名称 *</label>
            <input v-model="form.name" type="text" class="form-control" placeholder="请输入角色名称" required>
          </div>
          <div class="form-group">
            <label>角色编码</label>
            <input v-model="form.code" type="text" class="form-control" placeholder="请输入角色编码">
          </div>
          <div class="form-group">
            <label>上级角色</label>
            <select v-model="form.parentId" class="form-control">
              <option value="">无（顶级角色）</option>
              <option v-for="r in availableParents" :key="r.id" :value="r.id">{{ r.name }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>描述</label>
            <textarea v-model="form.description" class="form-control" rows="2" placeholder="请输入角色描述"></textarea>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeFormModal" class="btn btn-default">取消</button>
          <button @click="saveForm" class="btn btn-primary">保存</button>
        </div>
      </div>
    </div>

    <!-- 添加成员弹窗 -->
    <div v-if="showMemberAddModal" class="modal-overlay" @click.self="closeMemberAddModal">
      <div class="modal-dialog member-dialog">
        <div class="modal-header">
          <h4>添加成员 - {{ selectedRoleName }}</h4>
          <button @click="closeMemberAddModal" class="modal-close">&times;</button>
        </div>
        <div class="modal-body">
          <div class="search-box member-search-box">
            <input v-model="memberAddSearch" type="text" placeholder="搜索用户姓名或账号...">
          </div>
          <div class="member-select-list">
            <label class="select-all-row">
              <input type="checkbox" :checked="isAllAvailableSelected" @change="toggleAllAvailable">
              <span>全选</span>
            </label>
            <label
              v-for="user in availableUsers"
              :key="user.id"
              class="select-item"
            >
              <input
                type="checkbox"
                :checked="pendingMemberIds.includes(user.id)"
                @change="togglePendingMember(user.id)"
              >
              <span>{{ user.name }} ({{ user.account }})</span>
            </label>
            <div v-if="availableUsers.length === 0" class="empty-tip">没有可添加的用户</div>
          </div>
        </div>
        <div class="modal-footer">
          <button @click="closeMemberAddModal" class="btn btn-default">取消</button>
          <button @click="confirmAddMembers" class="btn btn-primary" :disabled="pendingMemberIds.length === 0">
            添加选中 ({{ pendingMemberIds.length }})
          </button>
        </div>
      </div>
    </div>

    <!-- 通用确认弹窗 -->
    <ConfirmModal
      :visible="showConfirm"
      :title="confirmTitle"
      :message="confirmMessage"
      confirmText="确认删除"
      type="danger"
      :loading="confirmLoading"
      @confirm="onConfirmDelete"
      @cancel="showConfirm = false"
    />

    <!-- 成员移除确认弹窗 -->
    <ConfirmModal
      :visible="showMemberConfirm"
      :title="memberConfirmTitle"
      :message="memberConfirmMessage"
      confirmText="确认移除"
      type="warning"
      :loading="memberConfirmLoading"
      @confirm="doRemoveMembers"
      @cancel="showMemberConfirm = false"
    />
  </div>
</template>

<script setup>
import { ref, h, reactive, computed, onMounted, watch } from 'vue'
import { getRoleTree, getRoleUsers, saveRole as saveRoleApi, deleteRole as deleteRoleApi, addRoleUsers, removeRoleUser } from '../api/role'
import { getUserList } from '../api/user'
import ConfirmModal from '../components/ConfirmModal.vue'

// 递归角色行组件
const RoleRow = {
  props: ['node', 'baseNo', 'depth', 'selectedId', 'expandedIds'],
  emits: ['select', 'edit', 'delete', 'addChild', 'toggleExpand'],
  setup(props, { emit }) {
    return () => {
      const { node, depth, selectedId, expandedIds } = props
      const hasChildren = node.children && node.children.length > 0
      const isExpanded = expandedIds.includes(node.id)

      return [
        h('tr', {
          class: ['role-row', { selected: selectedId === node.id }],
          onClick: () => emit('select', node)
        }, [
          h('td', { style: { paddingLeft: (depth * 24 + 8) + 'px' } }, [
            h('span', { class: 'role-name-cell' }, [
              hasChildren
                ? h('span', {
                    class: ['expand-icon', { expanded: isExpanded }],
                    onClick: (e) => { e.stopPropagation(); emit('toggleExpand', node.id) }
                  }, [
                    h('i', { class: 'fas fa-caret-right' })
                  ])
                : h('span', { class: 'expand-placeholder' }),
              h('i', { class: ['fas', 'fa-user-tag'] }),
              h('span', { class: 'role-name' }, node.name)
            ])
          ]),
          h('td', { class: 'action-cell' }, [
            h('button', { class: 'action-btn add', title: '添加子角色', onClick: (e) => { e.stopPropagation(); emit('addChild', node.id) } }, [
              h('i', { class: 'fas fa-plus' })
            ]),
            h('button', { class: 'action-btn edit', title: '编辑', onClick: (e) => { e.stopPropagation(); emit('edit', node) } }, [
              h('i', { class: 'fas fa-edit' })
            ]),
            h('button', { class: 'action-btn delete', title: '删除', onClick: (e) => { e.stopPropagation(); emit('delete', node) } }, [
              h('i', { class: 'fas fa-trash' })
            ])
          ])
        ]),
        hasChildren && isExpanded
          ? node.children.map(child =>
              h(RoleRow, {
                key: child.id,
                node: child,
                baseNo: child.no,
                depth: depth + 1,
                selectedId: selectedId,
                expandedIds: expandedIds,
                onSelect: (n) => emit('select', n),
                onEdit: (n) => emit('edit', n),
                onDelete: (n) => emit('delete', n),
                onAddChild: (id) => emit('addChild', id),
                onToggleExpand: (id) => emit('toggleExpand', id)
              })
            )
          : null
      ]
    }
  }
}

const treeSearch = ref('')
const selectedRoleId = ref(null)
const showFormModal = ref(false)
const showMemberAddModal = ref(false)
const editingId = ref(null)
const memberAddSearch = ref('')
const pendingMemberIds = ref([])
const selectedMemberIds = ref([])
const expandedIds = ref([])

const form = reactive({
  no: 0,
  name: '',
  code: '',
  description: '',
  parentId: ''
})

// 确认弹窗（删除角色）
const showConfirm = ref(false)
const confirmLoading = ref(false)
const confirmTitle = ref('')
const confirmMessage = ref('')
const pendingDeleteNode = ref(null)

// 确认弹窗（移除成员）
const showMemberConfirm = ref(false)
const memberConfirmLoading = ref(false)
const memberConfirmTitle = ref('')
const memberConfirmMessage = ref('')
let pendingMemberRemoveIds = []

const allUsers = ref([])
const roles = ref([])

const loadUsers = async () => {
  try {
    const res = await getUserList()
    allUsers.value = (res.data || res.Data || []).map(u => ({
      id: u.account || u.Account,
      account: u.account || u.Account,
      name: u.displayName || u.DisplayName,
      guid: u.guid || u.Guid
    }))
  } catch (e) { console.error('加载用户失败', e) }
}

const mapNode = (n) => ({
  id: n.guid || n.Guid,
  name: n.roleName || n.RoleName,
  code: n.roleCode || n.RoleCode || '',
  description: n.description || n.Description || '',
  no: n.no ?? n.No ?? 0,
  children: (n.children || n.Children || []).map(mapNode),
  members: [],
  _loaded: false
})

const loadRoles = async () => {
  try {
    const res = await getRoleTree(treeSearch.value || undefined)
    roles.value = (res.data || res.Data || []).map(mapNode)
    expandedIds.value = roles.value.map(r => r.id)
  } catch (e) { console.error('加载角色失败', e) }
}

onMounted(() => { loadRoles(); loadUsers() })

watch(treeSearch, () => {
  loadRoles()
})

const filteredTree = computed(() => {
  // 搜索已在后端完成，直接返回
  return roles.value
})

const selectedRole = computed(() => {
  const find = (list, id) => {
    for (const r of list) {
      if (r.id === id) return r
      if (r.children && r.children.length > 0) {
        const found = find(r.children, id)
        if (found) return found
      }
    }
    return null
  }
  return find(roles.value, selectedRoleId.value)
})

const selectedRoleName = computed(() => selectedRole.value ? selectedRole.value.name : '')
const selectedRoleDesc = computed(() => selectedRole.value ? selectedRole.value.description : '')

const currentMembers = computed(() => {
  if (!selectedRole.value) return []
  return selectedRole.value.members || []
})

const currentMemberIds = computed(() => currentMembers.value.map(m => m.id))

const availableUsers = computed(() => {
  const excludeIds = currentMemberIds.value
  let available = allUsers.value.filter(u => !excludeIds.includes(u.id))
  if (memberAddSearch.value) {
    const s = memberAddSearch.value.toLowerCase()
    available = available.filter(u => u.name.toLowerCase().includes(s) || u.account.toLowerCase().includes(s))
  }
  return available
})

const isAllAvailableSelected = computed(() => {
  return availableUsers.value.length > 0 && availableUsers.value.every(u => pendingMemberIds.value.includes(u.id))
})

const isAllMemberSelected = computed(() => {
  return currentMembers.value.length > 0 && currentMembers.value.every(m => selectedMemberIds.value.includes(m.id))
})

const formTitle = computed(() => editingId.value ? '修改角色' : '新增角色')

// 收集所有可选上级角色
const availableParents = computed(() => {
  const excludeId = editingId.value
  const list = []
  const collect = (nodes, prefix = '') => {
    nodes.forEach(n => {
      if (n.id !== excludeId) {
        list.push({ id: n.id, name: prefix ? `${prefix} / ${n.name}` : n.name })
      }
      if (n.children && n.children.length > 0) {
        collect(n.children, prefix ? `${prefix} / ${n.name}` : n.name)
      }
    })
  }
  collect(roles.value)
  return list
})

function toggleExpand(id) {
  const idx = expandedIds.value.indexOf(id)
  if (idx !== -1) expandedIds.value.splice(idx, 1)
  else expandedIds.value.push(id)
}

async function selectRole(role) {
  selectedRoleId.value = role.id
  selectedMemberIds.value = []
  try {
    const res = await getRoleUsers(role.id)
    role.members = (res.data || res.Data || []).map(m => ({
      id: m.account || m.Account,
      account: m.account || m.Account,
      name: m.displayName || m.DisplayName
    }))
  } catch (e) { console.error('加载角色成员失败', e) }
}

function openAddDialog(parentId) {
  editingId.value = null
  form.no = 0
  form.name = ''
  form.code = ''
  form.description = ''
  form.parentId = parentId || ''
  showFormModal.value = true
}

function openEditDialog(role) {
  editingId.value = role.id
  form.no = role.no
  form.name = role.name
  form.code = role.code || ''
  form.description = role.description || ''
  form.parentId = ''
  // 查找上级
  const findParent = (nodes, childId) => {
    for (const n of nodes) {
      if (n.children && n.children.some(c => c.id === childId)) return n.id
      if (n.children) {
        const p = findParent(n.children, childId)
        if (p) return p
      }
    }
    return ''
  }
  form.parentId = findParent(roles.value, role.id)
  showFormModal.value = true
}

function confirmDelete(node) {
  pendingDeleteNode.value = node
  confirmTitle.value = '确认删除角色'
  confirmMessage.value = `确定要删除角色「${node.name}」吗？删除后成员将失去此角色关联。`
  showConfirm.value = true
}

async function onConfirmDelete() {
  if (!pendingDeleteNode.value) return
  confirmLoading.value = true
  try {
    await deleteRoleApi(pendingDeleteNode.value.id)
    if (selectedRoleId.value === pendingDeleteNode.value.id) selectedRoleId.value = null
    showConfirm.value = false
    await loadRoles()
  } catch (e) {
    $message.error('删除失败：' + e.message)
  } finally {
    confirmLoading.value = false
  }
}

async function saveForm() {
  if (!form.name) {
    $message.warning('请填写角色名称')
    return
  }
  if (form.no <= 0) {
    $message.warning('请填写序号')
    return
  }
  const payload = {
    Guid: editingId.value || '',
    RoleName: form.name,
    RoleCode: form.code,
    Description: form.description,
    HigherGuid: form.parentId || '',
    No: form.no,
    Active: 1
  }
  try {
    await saveRoleApi(payload)
    closeFormModal()
    await loadRoles()
  } catch (e) { $message.error('保存失败：' + e.message) }
}

function closeFormModal() {
  showFormModal.value = false
  editingId.value = null
  form.no = 0
  form.name = ''
  form.code = ''
  form.description = ''
  form.parentId = ''
}

function openMemberAdd() {
  pendingMemberIds.value = []
  memberAddSearch.value = ''
  showMemberAddModal.value = true
}

function closeMemberAddModal() {
  showMemberAddModal.value = false
  pendingMemberIds.value = []
  memberAddSearch.value = ''
}

function togglePendingMember(userId) {
  const idx = pendingMemberIds.value.indexOf(userId)
  if (idx === -1) pendingMemberIds.value.push(userId)
  else pendingMemberIds.value.splice(idx, 1)
}

function toggleAllAvailable() {
  if (isAllAvailableSelected.value) pendingMemberIds.value = []
  else pendingMemberIds.value = availableUsers.value.map(u => u.id)
}

async function confirmAddMembers() {
  if (!selectedRole.value) return
  const usersToAdd = allUsers.value.filter(u => pendingMemberIds.value.includes(u.id))
  if (usersToAdd.length === 0) { closeMemberAddModal(); return }
  const payload = usersToAdd.map(u => ({ Guid: u.guid, Account: u.account, DisplayName: u.name }))
  try {
    await addRoleUsers(selectedRole.value.id, payload)
    closeMemberAddModal()
    await selectRole(selectedRole.value)
  } catch (e) { $message.error('添加成员失败：' + e.message) }
}

function toggleMemberSelect(memberId) {
  const idx = selectedMemberIds.value.indexOf(memberId)
  if (idx === -1) selectedMemberIds.value.push(memberId)
  else selectedMemberIds.value.splice(idx, 1)
}

function toggleAllMember() {
  if (isAllMemberSelected.value) selectedMemberIds.value = []
  else selectedMemberIds.value = currentMembers.value.map(m => m.id)
}

async function removeSingleMember(memberId) {
  if (!selectedRole.value) return
  const member = currentMembers.value.find(m => m.id === memberId)
  if (!member) return

  memberConfirmTitle.value = '确认移除成员'
  memberConfirmMessage.value = `确定要移除成员「${member.name}(${member.account})」吗？`
  pendingMemberRemoveIds = [memberId]
  showMemberConfirm.value = true
}

async function doRemoveMembers() {
  if (!selectedRole.value || pendingMemberRemoveIds.length === 0) return
  memberConfirmLoading.value = true
  try {
    const targets = currentMembers.value.filter(m => pendingMemberRemoveIds.includes(m.id))
    for (const m of targets) {
      await removeRoleUser(selectedRole.value.id, m.account)
    }
    selectedMemberIds.value = selectedMemberIds.value.filter(id => !pendingMemberRemoveIds.includes(id))
    showMemberConfirm.value = false
    await selectRole(selectedRole.value)
  } catch (e) { $message.error('移除失败：' + e.message) }
  finally { memberConfirmLoading.value = false }
}

async function batchRemoveMembers() {
  if (!selectedRole.value || selectedMemberIds.value.length === 0) return

  memberConfirmTitle.value = '确认批量移除'
  memberConfirmMessage.value = `确定要移除选中的 ${selectedMemberIds.value.length} 个成员吗？`
  pendingMemberRemoveIds = [...selectedMemberIds.value]
  showMemberConfirm.value = true
}
</script>

<style scoped>
.view-container {
  padding: 16px;
  background: #f5f7fa;
  height: calc(100vh - 150px);
  box-sizing: border-box;
  overflow: hidden;
}

.main-layout {
  display: flex;
  gap: 16px;
  height: 100%;
}

.left-panel {
  flex: 0 0 22%;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e8ecf1;
  display: flex;
  flex-direction: column;
}

.right-panel {
  flex: 1;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #e8ecf1;
  display: flex;
  flex-direction: column;
}

.panel-toolbar {
  padding: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #e8ecf1;
  background: #fafbfc;
}

.panel-body {
  flex: 1;
  padding: 8px;
  overflow-y: auto;
}

.search-box {
  position: relative;
  flex: 1;
}

.search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 12px;
}

.search-input {
  width: 100%;
  padding: 6px 10px 6px 28px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  transition: border-color 0.15s;
}

.search-input:focus { border-color: #1ab394; }

.btn-add {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #1ab394;
  border: none;
  border-radius: 4px;
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
}

.btn-add:hover { background: #17a689; }

.role-table {
  width: 100%;
  border-collapse: collapse;
}

.role-table thead th {
  padding: 8px 10px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  background: #f8fafc;
  border-bottom: 1px solid #e8ecf1;
}

.role-table tbody td {
  padding: 6px 10px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 12px;
  color: #374151;
}

.role-row {
  cursor: pointer;
  transition: background 0.1s;
}

.role-row:hover { background: #f9fafb; }
.role-row.selected { background: #ecfdf5; }
.role-row.selected td { color: #059669; }

.empty-row {
  text-align: center;
  padding: 20px;
  color: #9ca3af;
}

.role-name-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}

.role-name-cell i {
  color: #10b981;
  font-size: 12px;
}

.role-name { font-size: 13px; font-weight: 500; }

.expand-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  cursor: pointer;
  transition: transform 0.2s;
  font-size: 12px;
  color: #9ca3af;
}

.expand-icon.expanded { transform: rotate(90deg); }

.expand-placeholder { display: inline-block; width: 16px; }

.action-cell { display: flex; gap: 4px; justify-content: flex-end; }

.action-btn {
  width: 22px;
  height: 22px;
  border: 1px solid #e5e7eb;
  border-radius: 3px;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  transition: all 0.15s;
}

.action-btn.add { color: #10b981; border-color: #d1fae5; }
.action-btn.add:hover { background: #10b981; color: #fff; border-color: #10b981; }

.action-btn.edit { color: #6366f1; border-color: #e0e7ff; }
.action-btn.edit:hover { background: #6366f1; color: #fff; border-color: #6366f1; }

.action-btn.delete { color: #ef4444; border-color: #fee2e2; }
.action-btn.delete:hover { background: #ef4444; color: #fff; border-color: #ef4444; }

.detail-header {
  padding: 16px;
  border-bottom: 1px solid #e8ecf1;
}

.detail-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
}

.detail-desc {
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
}

.detail-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;
}

.detail-subtitle {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
}

.btn-add-member {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  background: #10b981;
  border: none;
  border-radius: 4px;
  color: #fff;
  font-size: 11px;
  cursor: pointer;
}
.btn-add-member:hover { background: #059669; }

.member-table { width: 100%; border-collapse: collapse; }

.member-table thead th {
  padding: 8px 10px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  background: #f8fafc;
  border-bottom: 1px solid #e8ecf1;
}

.member-table tbody td {
  padding: 8px 10px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 12px;
  color: #374151;
}

.member-check-th { text-align: center; }
.member-check-th input[type="checkbox"] {
  accent-color: #10b981;
  width: 14px;
  height: 14px;
  cursor: pointer;
}

.btn-remove {
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 3px;
  background: #fee2e2;
  color: #ef4444;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
}
.btn-remove:hover { background: #ef4444; color: #fff; }

.no-members {
  text-align: center;
  padding: 24px;
  color: #9ca3af;
}
.no-members p { margin: 0 0 10px; font-size: 13px; }

.btn-add-member-sm {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 5px 10px;
  background: #10b981;
  border: none;
  border-radius: 4px;
  color: #fff;
  font-size: 11px;
  cursor: pointer;
}

.detail-footer {
  padding: 10px 16px;
  border-top: 1px solid #e8ecf1;
  display: flex;
  justify-content: flex-end;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9ca3af;
}

.empty-state i { font-size: 32px; margin-bottom: 12px; opacity: 0.5; }
.empty-state p { margin: 4px 0; font-size: 13px; }
.empty-state .hint { font-size: 11px; color: #d1d5db; }

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-dialog {
  background: #fff;
  border-radius: 6px;
  width: 420px;
  max-width: 90vw;
}
.modal-dialog.member-dialog { width: 480px; }

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 14px;
  border-bottom: 1px solid #e8ecf1;
}
.modal-header h4 { margin: 0; font-size: 13px; font-weight: 600; color: #1f2937; }

.modal-close {
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  font-size: 18px;
  color: #9ca3af;
  cursor: pointer;
}
.modal-close:hover { color: #374151; }

.modal-body { padding: 14px; }

.form-group { margin-bottom: 12px; }
.form-group label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
}

.form-control {
  width: 100%;
  padding: 7px 10px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.form-control:focus { border-color: #1ab394; }
textarea.form-control { resize: vertical; }

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 10px 14px;
  border-top: 1px solid #e8ecf1;
}

.search-box { position: relative; }
.search-box input {
  width: 100%;
  padding: 8px 10px 8px 30px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 13px;
  outline: none;
  box-sizing: border-box;
}
.search-box::before {
  content: '\f002';
  font-family: 'Font Awesome 5 Free';
  font-weight: 900;
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
  font-size: 12px;
  z-index: 1;
  pointer-events: none;
}
.search-box input:focus { border-color: #1ab394; }

.member-select-list { max-height: 240px; overflow-y: auto; }

.select-all-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
  border-bottom: 1px solid #f3f4f6;
  margin-bottom: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: #374151;
}
.select-all-row input { accent-color: #10b981; width: 14px; height: 14px; }

.select-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 4px;
  cursor: pointer;
  font-size: 12px;
  border-radius: 3px;
}
.select-item:hover { background: #f9fafb; }
.select-item input { accent-color: #10b981; width: 14px; height: 14px; }

.empty-tip {
  text-align: center;
  padding: 16px;
  color: #9ca3af;
  font-size: 12px;
}

.btn { font-size: 12px; padding: 6px 14px; border-radius: 4px; cursor: pointer; border: 1px solid transparent; }
.btn-default { background: #f3f4f6; border-color: #d1d5db; color: #374151; }
.btn-default:hover { background: #e5e7eb; }
.btn-primary { background: #1ab394; border-color: #1ab394; color: #fff; }
.btn-primary:hover { background: #17a689; }
.btn-danger { background: #ef4444; border-color: #ef4444; color: #fff; }
.btn-danger:hover { background: #dc2626; }
</style>

<style>
/* 非 scoped 样式 — RoleRow render 函数组件不受 scoped CSS 影响 */
.role-table .role-row { cursor: pointer; transition: background 0.1s; }
.role-table .role-row:hover { background: #f9fafb; }
.role-table .role-row.selected { background: #ecfdf5; }
.role-table .role-row.selected td { color: #059669; }
.role-table .empty-row { text-align: center; padding: 20px; color: #9ca3af; }

.role-table .role-name-cell { display: flex; align-items: center; gap: 6px; }
.role-table .role-name-cell i { color: #10b981; font-size: 12px; }
.role-table .role-name { font-size: 13px; font-weight: 500; }

.role-table .expand-icon { display: inline-flex; align-items: center; justify-content: center; width: 16px; height: 16px; cursor: pointer; transition: transform 0.2s; font-size: 12px; color: #9ca3af; }
.role-table .expand-icon.expanded { transform: rotate(90deg); }
.role-table .expand-placeholder { display: inline-block; width: 16px; }

.role-table .action-cell { display: flex; gap: 4px; justify-content: flex-end; }
.role-table .action-btn { width: 22px; height: 22px; border: 1px solid #e5e7eb; border-radius: 3px; background: #fff; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 10px; transition: all 0.15s; }
.role-table .action-btn.add { color: #10b981; border-color: #d1fae5; }
.role-table .action-btn.add:hover { background: #10b981; color: #fff; border-color: #10b981; }
.role-table .action-btn.edit { color: #6366f1; border-color: #e0e7ff; }
.role-table .action-btn.edit:hover { background: #6366f1; color: #fff; border-color: #6366f1; }
.role-table .action-btn.delete { color: #ef4444; border-color: #fee2e2; }
.role-table .action-btn.delete:hover { background: #ef4444; color: #fff; border-color: #ef4444; }
</style>
